// homeLayout.js — Home declutter rules (2026-10-20), pure and unit-tested.
// Order on Home: rings → Up next (max UP_NEXT_MAX) → Kai brief (collapsed to
// briefHeadline + Read more) → Your week (one line, expands on tap).

export const UP_NEXT_MAX = 3;

// Fill Up next in priority order — needs an answer, today, this week, bills
// due soon — until `max` rows are taken. Each group keeps its own order.
export function takeUpNext({ needAnswer = [], today = [], week = [], bills = [] } = {}, max = UP_NEXT_MAX) {
  let budget = max;
  const take = (arr) => { const n = (arr || []).slice(0, Math.max(0, budget)); budget -= n.length; return n; };
  return { needAnswer: take(needAnswer), today: take(today), week: take(week), bills: take(bills) };
}

// The collapsed brief's one line: the check-in headline, else the client
// brief's greeting, else nothing to collapse to (the card shows Read more only).
export function briefHeadline(json) {
  if (!json || typeof json !== 'object') return null;
  const h = typeof json.headline === 'string' && json.headline.trim() ? json.headline.trim() : null;
  if (h) return h;
  const g = typeof json.greeting === 'string' && json.greeting.trim() ? json.greeting.trim() : null;
  return g;
}

// ── HOME HEADER (2026-10-22) ─────────────────────────────────────────────────
// The compact date pad + greeting + "One thing" line that replaced the date
// line and greeting block. Pure, unit-tested.

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTHS_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const WD = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const WD_LONG = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/** The pad's parts for a local date 'YYYY-MM-DD': { month:'Oct', day:22, weekday:'Thu', aria:'Open calendar, Thursday October 22' }. */
export function dayPadParts(dateStr) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(dateStr || ''));
  if (!m) return null;
  const d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
  const mi = d.getUTCMonth(), wi = d.getUTCDay(), day = d.getUTCDate();
  return { month: MONTHS[mi], day, weekday: WD[wi], aria: `Open calendar, ${WD_LONG[wi]} ${MONTHS_LONG[mi]} ${day}` };
}

/**
 * THE Up next grouping, shared by the Home card and the header's fallback:
 * needs an answer, due today, due this week (≤7 days), bills due within 3
 * days that the user must act on (not autopay, not paid). Each group keeps
 * its input order. `daysFromToday(dateStr)` is the caller's local-day diff.
 */
export function upNextGroups({ commitments = [], annotatedBills = [], todayStr, daysFromToday = () => null } = {}) {
  const active = (commitments || []).filter(c => c && (c.status === 'open' || c.status === 'needs_confirmation'));
  return {
    needAnswer: active.filter(c => c.status === 'needs_confirmation'),
    today: active.filter(c => c.status === 'open' && c.due_date === todayStr),
    week: active.filter(c => c.status === 'open' && c.due_date && c.due_date > todayStr && (daysFromToday(c.due_date) ?? 99) <= 7),
    bills: (annotatedBills || []).filter(b => b && b.daysUntil != null && b.daysUntil >= 0 && b.daysUntil <= 3 && b.autopay !== true && !b.paidThisMonth),
  };
}

/** The first Up next item's label in the card's priority order, or null. */
export function firstUpNextLabel(groups, { commitLabel = (c) => c?.text || '', fmt12h = () => null } = {}) {
  if (!groups) return null;
  const c = groups.needAnswer?.[0] || groups.today?.[0] || groups.week?.[0];
  if (c) { const t = c.due_time ? fmt12h(c.due_time) : null; return `${commitLabel(c)}${t ? ` at ${t}` : ''}`; }
  const b = groups.bills?.[0];
  if (b) return `${b.name} is due ${b.daysUntil === 0 ? 'today' : b.daysUntil === 1 ? 'tomorrow' : `in ${b.daysUntil} days`}`;
  return null;
}

/**
 * "One thing: …" under the greeting: Kai's brief oneThing when there is one,
 * else the first Up next item, else nothing (null → the line is not shown).
 * Copy rule: no dashes; a trailing period is dropped so the line reads as a
 * label.
 */
export function pickOneThing({ oneThing, upNextLabel } = {}) {
  const clean = (s) => String(s || '').replace(/\s*[—–]\s*|\s+--\s+/g, ', ').replace(/\s+/g, ' ').trim().replace(/[.]+$/, '');
  const a = clean(oneThing);
  if (a) return a;
  const b = clean(upNextLabel);
  return b || null;
}
